/**
 * Frontend API Service — all calls go to Express /api/*.
 * Admin endpoints require JWT token from sessionStorage.
 * No Supabase dependency.
 */

// ── Auth token management ───────────────────────────────────────────────────

function getToken() {
  return sessionStorage.getItem('zba_admin_token');
}

function setToken(token) {
  sessionStorage.setItem('zba_admin_token', token);
}

function clearToken() {
  sessionStorage.removeItem('zba_admin_token');
}

function authHeaders() {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function adminFetch(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: { ...authHeaders(), ...options.headers },
  });
  if (res.status === 401) {
    clearToken();
    throw new Error('SESSION_EXPIRED');
  }
  return res;
}

// ── Admin Login ─────────────────────────────────────────────────────────────

export async function adminLogin(password) {
  const res = await fetch('/api/admin/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  const data = await res.json();
  if (data.token) {
    setToken(data.token);
    return { success: true };
  }
  return { success: false, error: data.error || 'Login fehlgeschlagen' };
}

export function isAuthenticated() {
  return !!getToken();
}

export function adminLogout() {
  clearToken();
}

// ── Settings ────────────────────────────────────────────────────────────────

export async function fetchAllSettings() {
  try {
    const res = await adminFetch('/api/admin/settings');
    if (!res.ok) return {};
    return await res.json();
  } catch (err) {
    if (err.message === 'SESSION_EXPIRED') throw err;
    console.error('[apiService] fetchAllSettings error:', err.message);
    return {};
  }
}

export async function saveAllSettings(sections) {
  try {
    const res = await adminFetch('/api/admin/settings', {
      method: 'POST',
      body: JSON.stringify(sections),
    });
    const data = await res.json();
    return data;
  } catch (err) {
    if (err.message === 'SESSION_EXPIRED') throw err;
    console.error('[apiService] saveAllSettings error:', err.message);
    return { success: false, error: err.message };
  }
}

// ── Bookings (admin) ────────────────────────────────────────────────────────

export async function fetchBookings(limitDays = null) {
  try {
    const url = limitDays ? `/api/admin/bookings?days=${limitDays}` : '/api/admin/bookings';
    const res = await adminFetch(url);
    if (!res.ok) return [];
    return await res.json();
  } catch (err) {
    if (err.message === 'SESSION_EXPIRED') throw err;
    console.error('[apiService] fetchBookings error:', err.message);
    return [];
  }
}

export async function updateBookingStatus(bookingRef, status, extra = {}) {
  try {
    const res = await adminFetch(`/api/admin/bookings/${bookingRef}/status`, {
      method: 'POST',
      body: JSON.stringify({ status, ...extra }),
    });
    return await res.json();
  } catch (err) {
    if (err.message === 'SESSION_EXPIRED') throw err;
    return { success: false, error: err.message };
  }
}

export async function deleteBooking(bookingRef) {
  try {
    const res = await adminFetch(`/api/admin/bookings/${bookingRef}`, {
      method: 'DELETE',
    });
    return await res.json();
  } catch (err) {
    if (err.message === 'SESSION_EXPIRED') throw err;
    return { deleted: false, error: err.message };
  }
}

// ── Booking (public — form submission) ──────────────────────────────────────

export async function insertBooking(bookingData) {
  try {
    const res = await fetch('/api/book', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bookingData),
    });
    const data = await res.json();
    return { success: data.success !== false, ...data };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

// ── Round Robin (public) ────────────────────────────────────────────────────

export async function advanceAndGetRRCounter() {
  // Handled server-side in /api/book now
  return 0;
}

// ── Booking Logs (admin) ────────────────────────────────────────────────────

export async function fetchBookingLogs(eventType = null) {
  try {
    const url = eventType ? `/api/admin/logs?type=${eventType}` : '/api/admin/logs';
    const res = await adminFetch(url);
    if (!res.ok) return [];
    return await res.json();
  } catch (err) {
    if (err.message === 'SESSION_EXPIRED') throw err;
    return [];
  }
}

// ── Step Logging (public — no auth) ─────────────────────────────────────────

export async function insertBookingLog(bookingRef, eventType, metadata = {}) {
  try {
    await fetch('/api/log-step', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ booking_ref: bookingRef, event_type: eventType, metadata }),
    });
    return { success: true };
  } catch {
    return { success: false };
  }
}

// ── Funnel Data (admin) ─────────────────────────────────────────────────────

export async function fetchFunnelData(dedupHours = 12) {
  try {
    const res = await adminFetch(`/api/admin/funnel?dedup_hours=${dedupHours}`);
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    if (err.message === 'SESSION_EXPIRED') throw err;
    return null;
  }
}

// ── Notifications (admin) ───────────────────────────────────────────────────

export async function fetchNotifications(limitHours = 72) {
  try {
    const res = await adminFetch(`/api/admin/notifications?hours=${limitHours}`);
    if (!res.ok) return [];
    return await res.json();
  } catch (err) {
    if (err.message === 'SESSION_EXPIRED') throw err;
    return [];
  }
}

// ── Admin Actions (admin) ───────────────────────────────────────────────────

export async function insertAdminAction(bookingRef, actionType, details = {}) {
  // Handled server-side in status update now
  return { success: true };
}

export async function fetchAdminActions(bookingRef = null) {
  try {
    const url = bookingRef ? `/api/admin/actions?booking_ref=${bookingRef}` : '/api/admin/actions';
    const res = await adminFetch(url);
    if (!res.ok) return [];
    return await res.json();
  } catch (err) {
    if (err.message === 'SESSION_EXPIRED') throw err;
    return [];
  }
}
